import { mergeTemplate } from './base-template';

export const treuhandStartupTemplate = mergeTemplate({
  name: 'Treuhand für Start-ups',
  icon: '🚀',
  settings: {
    primaryColor: '#0d9488',
    accentColor: '#38bdf8',
    fontStack: 'spark',
    stylePreset: 'gasserwerk',
    pageAngebot: {
      heroTitle: 'Start-up-Pakete',
      heroSubtitle: 'Finance-Support, der mit euch skaliert',
      packages: [
        { name: 'Gründung', price: "CHF 1'200 einmalig", description: 'Alles für den sauberen Start', features: 'Firmengründung, Handelsregister, Bankkonto-Setup, Buchhaltungs-Einrichtung', highlighted: false },
        { name: 'Wachstum', price: 'ab CHF 490/Mt.', description: 'Laufende Finanzen für Seed bis Series A', features: 'Buchhaltung, MWST, Lohn, Investor-Reporting, Budget-Tracking', highlighted: true },
        { name: 'Scale-up', price: "ab CHF 1'200/Mt.", description: 'CFO-Level Support ohne Fixkosten', features: 'Wachstum + Controlling, Cashflow-Planung, Board-Reporting, Fundraising-Support', highlighted: false },
      ],
      ctaTitle: 'Bereit für saubere Finanzen?',
      ctaText: 'Wir kennen die Start-up-Realität und liefern, was ihr braucht — ohne Overhead.',
      ctaButtonText: "Let's talk",
    },
    pageMethoden: {
      heroTitle: 'Unser Approach',
      heroSubtitle: 'Lean, digital, founder-friendly',
      introText: 'Wir arbeiten wie ein internes Finance-Team — nur ohne die Fixkosten. Agil, toolbasiert und auf eure Milestones ausgerichtet.',
      steps: [
        { title: 'Kick-off', description: '30-Min. Call: Eure Situation, eure Ziele, unser Setup-Vorschlag' },
        { title: 'Onboarding', description: 'Tool-Setup, Belegprozess, erste Buchungen innerhalb 48h' },
        { title: 'Laufend', description: 'Monatliche Buchhaltung, Dashboards, proaktive Hinweise' },
        { title: 'Skalierung', description: 'Anpassung an euer Wachstum — mehr Mitarbeitende, Investoren, Komplexität' },
      ],
      tools: [
        { name: 'Bexio', description: 'Buchhaltung und Rechnungen in der Cloud' },
        { name: 'Notion', description: 'Gemeinsame Dokumentation und Prozesse' },
        { name: 'Slack', description: 'Schnelle Kommunikation statt E-Mail-Ping-Pong' },
      ],
      philosophyTitle: 'Start-up-DNA',
      philosophyText: 'Wir sind selbst Gründer. Wir wissen, dass Cash King ist und Bürokratie Gift. Deshalb halten wir alles schlank.',
    },
    pageUeberMich: {
      heroTitle: 'Das Team',
      heroSubtitle: 'Gründer-Mindset trifft Treuhand-Kompetenz',
      introText: 'Wir haben selbst gegründet und verstehen, was Founder brauchen: Schnelligkeit, Klarheit und einen Partner, der mitdenkt.',
      imageUrl: '',
      timeline: [
        { year: '2018', text: 'Gründung aus dem Start-up-Ökosystem heraus' },
        { year: '2020', text: 'Erste 50 Start-up-Mandate' },
        { year: '2023', text: 'Partnerships mit Inkubatoren und VCs' },
      ],
      values: [
        { title: 'Speed', description: 'Antworten in Stunden, nicht Wochen' },
        { title: 'Klartext', description: 'Finance auf Deutsch erklärt, nicht in Fachsprache versteckt' },
        { title: 'Flexibilität', description: 'Wir passen uns eurem Tempo an — nicht umgekehrt' },
      ],
      certifications: 'Treuhand Suisse, Bexio-zertifiziert, Startup-Ökosystem Schweiz',
    },
  },
  components: {
    header: {
      companyName: 'Treuhand Muster AG',
      logoText: 'TM',
      navItems: ['Leistungen', 'Methoden', 'Team', 'Kontakt'],
      ctaText: 'Erstgespräch buchen',
    },
    hero: {
      title: 'Finance & Treuhand, die mitwächst',
      subtitle:
        'Setup, Reporting und Steuern – mit Cloud-Tools und pragmatischem Sparring.',
      primaryBtn: 'Kostenloses Erstgespräch',
      secondaryBtn: 'Start-up Setup',
      imageUrl: '',
    },
    services: {
      title: 'Start-up Services',
      subtitle: 'Vom Setup bis zum Reporting – skalierbar.',
      items: [
        {
          icon: '🧩',
          title: 'Tooling & Setup',
          text: 'Cloud-Buchhaltung, Belegfluss, Bank-Feeds – sauber aufgesetzt.',
        },
        {
          icon: '📈',
          title: 'Cash & Reporting',
          text: 'Monatliche Auswertungen, KPIs und Entscheidungsgrundlagen.',
        },
        {
          icon: '🧾',
          title: 'Steuern & MWST',
          text: 'MWST-Setup, Erklärungen, Planung – ohne Overhead.',
        },
        {
          icon: '🤝',
          title: 'Sparring',
          text: 'Finanzübersetzer für Gründerteams, Investorenfragen, Struktur.',
        },
      ],
    },
    proofTable: {
      title: 'Warum Start-ups uns wählen',
      subtitle: 'Weniger Reibung, mehr Klarheit.',
      headers: ['Kriterium', 'Mit uns', 'Ohne Setup'],
      highlightColumn: 1,
      rows: [
        {
          criterion: 'Time-to-Insights',
          us: 'KPIs und Reports in klaren Zyklen',
          other: 'Zahlen kommen spät oder gar nicht',
        },
        {
          criterion: 'Tool-Fit',
          us: 'Cloud-Stack passend zum Team',
          other: 'Tool-Wildwuchs / Medienbrüche',
        },
        {
          criterion: 'Investor Readiness',
          us: 'Strukturierte Daten & Unterlagen',
          other: 'Nacharbeiten unter Druck',
        },
      ],
    },
    team: {
      title: 'Euer Finance-Team',
      subtitle: 'Gründer-Erfahrung trifft Treuhand-Know-how.',
      members: [
        {
          name: 'Fabian Moser',
          role: 'Gründer & Start-up-Berater',
          image: '',
          bio: 'Selbst zweifacher Gründer mit Treuhand-Hintergrund. Versteht Founder-Alltag, Fundraising und die Sprache der Investoren.',
        },
        {
          name: 'Lena Bachmann',
          role: 'Buchhaltung & Reporting',
          image: '',
          bio: 'Spezialisiert auf Cloud-Buchhaltung und Start-up-Reporting. Bringt Ordnung in schnell wachsende Strukturen.',
        },
      ],
    },
    testimonials: {
      title: 'Was Founder sagen',
      items: [
        {
          quote: 'Endlich ein Treuhänder, der unsere Sprache spricht und nicht in Fachbegriffen redet.',
          author: 'A. Bieri',
          role: 'Co-Founder, SaaS-Start-up',
        },
        {
          quote: 'Das Onboarding war in zwei Tagen erledigt — inklusive Bexio-Setup und Belegprozess.',
          author: 'N. Kuster',
          role: 'CEO, HealthTech',
        },
        {
          quote: 'Dank den monatlichen Reports konnten wir unsere Series A bestens vorbereitet angehen.',
          author: 'L. Meier',
          role: 'Gründerin, FinTech',
        },
        {
          quote: 'Flexibel, schnell und immer einen Schritt voraus. Genau der Partner, den ein Start-up braucht.',
          author: 'T. Sutter',
          role: 'CTO, CleanTech Start-up',
        },
      ],
    },
    faq: {
      title: 'FAQ für Founder',
      subtitle: 'Die wichtigsten Fragen zum Start.',
      items: [
        {
          question: 'Wann braucht ein Start-up einen Treuhänder?',
          answer:
            'Am besten ab Gründung. Saubere Strukturen von Anfang an sparen später viel Zeit und Geld — besonders vor einer Finanzierungsrunde.',
        },
        {
          question: 'Wie schnell könnt ihr loslegen?',
          answer:
            'Nach dem Kick-off-Call sind wir typischerweise innerhalb von 48 Stunden einsatzbereit. Tool-Setup und erste Buchungen inklusive.',
        },
        {
          question: 'Unterstützt ihr bei Fundraising und Investor-Reporting?',
          answer:
            'Ja. Wir erstellen saubere Monats- und Quartalsberichte, Cashflow-Prognosen und bereiten Datenräume für Due Diligence vor.',
        },
      ],
    },
  },
});
